import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/kedve/Reveal";

const PILLARS = [
  { code: "E.01", title: "Corporate Academies", body: "A learning system built inside your organisation, shaped around your business and taught by our team." },
  { code: "E.02", title: "Leadership Development", body: "Cohort programs for the professionals you are betting on next. Sequenced across the arc of a role, not a quarter." },
  { code: "E.03", title: "Role Architectures", body: "The map of what each role needs to do, know and become. A shared language for growth across the company." },
  { code: "E.04", title: "Executive Fellowships", body: "Small rooms for senior leaders. Serious work on the questions they cannot ask anywhere else." },
  { code: "E.05", title: "Capability Reviews", body: "A quiet, senior-level diagnostic of where your talent is strong, where it is thin and what to do about it." },
];

export default function Enterprise() {
  return (
    <div data-testid="page-enterprise" className="bg-black text-white">
      {/* HERO */}
      <section className="pt-36 pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="eyebrow text-white/50">For Business</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl lg:text-[9.5rem] leading-[0.9] tracking-tightest">
            Capability, built<br /><span className="italic">into the business.</span>
          </h1>
          <div className="mt-14 grid md:grid-cols-12 gap-10">
            <p className="md:col-span-6 text-lg text-white/70 leading-relaxed">
              We work with organisations that want to develop leaders from within. Programs designed with your business, taught by our team, and built to outlast a launch cycle.
            </p>
            <div className="md:col-span-4 md:col-start-9 flex flex-col md:items-end gap-3">
              <a href="#contact" data-testid="enterprise-speak" className="inline-flex items-center gap-2 bg-white text-black rounded-full px-7 py-4 text-sm tracking-wide hover:bg-white/90 transition">
                Speak with kedve <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#review" data-testid="enterprise-review" className="inline-flex items-center gap-2 border border-white/50 rounded-full px-7 py-4 text-sm tracking-wide hover:border-white transition">
                Request a Capability Review
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="eyebrow text-white/50">What we do</div>
              <h2 className="mt-6 font-serif text-5xl md:text-6xl tracking-tightest leading-[0.95]">Five pillars.<br /><span className="italic">One programme.</span></h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-lg leading-relaxed text-white/70 self-end">
              Enterprises do not need more content. They need a shared way to grow their people. We build it with you.
            </p>
          </div>

          <div className="border-t border-white/15">
            {PILLARS.map((p, i) => (
              <Reveal key={p.code} delay={i * 60}>
                <div
                  className="grid md:grid-cols-12 gap-6 py-10 border-b border-white/15 items-baseline"
                  id={p.code === "E.04" ? "fellowship" : undefined}
                  data-testid={`pillar-${p.code}`}
                >
                  <div className="md:col-span-2 font-mono text-xs text-white/50">{p.code}</div>
                  <h3 className="md:col-span-5 font-serif text-3xl md:text-5xl tracking-tightest">{p.title}</h3>
                  <p className="md:col-span-5 text-white/70 text-base md:text-lg leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEW */}
      <section id="review" className="bg-white text-black py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-6">
              <div className="eyebrow opacity-60">The kedve Capability Review</div>
              <h2 className="mt-6 font-serif text-5xl md:text-6xl tracking-tightest leading-[0.95]">A diagnostic before<br /><span className="italic">a decision.</span></h2>
              <p className="mt-8 text-lg leading-relaxed opacity-70 max-w-md">
                Six quiet weeks. Senior interviews. A written memo. No slide decks.
              </p>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <ol className="space-y-6">
                {[
                  "Conversations with your top leaders",
                  "A read on what your talent is really strong at",
                  "A read on where it is thin",
                  "A benchmark against the businesses that get this right",
                  "A written memo, with a small set of clear moves",
                ].map((step, i) => (
                  <li key={i} className="flex gap-4 items-start border-t border-black/20 pt-4">
                    <span className="font-mono text-xs opacity-60 pt-1">0{i + 1}</span>
                    <span className="text-base md:text-lg leading-snug">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-32 md:py-44">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <div className="eyebrow text-white/50">Begin the conversation</div>
          <h2 className="mt-8 font-serif text-5xl md:text-8xl tracking-tightest leading-[0.9]">
            Develop leaders,<br /><span className="italic">quietly and well.</span>
          </h2>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <a href="mailto:learn@kedve.com" data-testid="cta-email" className="inline-flex items-center gap-2 bg-white text-black rounded-full px-8 py-4 text-sm tracking-wide hover:bg-white/90 transition">
              learn@kedve.com <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link to="/apply" className="inline-flex items-center gap-2 border border-white/50 hover:border-white rounded-full px-8 py-4 text-sm tracking-wide transition">
              Individual applications
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
